import { toMoneyAmount } from '@/common/domain/money/toMoneyAmount';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The context bags whose every value is a currency / number variable (see the `number`-typed catalog paths). */
const NUMERIC_BAGS = ['totals'] as const;

/** Flags that live in a numeric bag but are not amounts: kept as a boolean, never coerced to a number. */
const FLAG_KEYS: ReadonlySet<string> = new Set(['has_line_items']);

/** Individual `number`-typed catalog paths outside those bags. */
const NUMERIC_PATHS: ReadonlyArray<readonly [string, string]> = [
  ['quote', 'valid_days'],
  ['scope', 'job_site_count'],
  ['scope', 'phase_count'],
  ['scope', 'line_item_count']
];

/**
 * Normalises the currency / number variables of a host context ("$3,680.00", "3680", null) to real
 * numbers, so no renderer or token ever sees a string where the contract says number. A value that
 * is not an amount at all becomes `null` (printed as an em dash), never NaN. Returns a shallow copy
 * with only the touched bags cloned.
 */
export function coerceNumericVariables(raw: Record<string, unknown>): Record<string, unknown> {
  let result = raw;
  const ensureBag = (bag: string): Record<string, unknown> | null => {
    const source = result[bag];
    if (!isPlainObject(source)) return null;
    if (result === raw) result = { ...raw };
    const copy = { ...source };
    result[bag] = copy;
    return copy;
  };

  for (const bag of NUMERIC_BAGS) {
    const copy = ensureBag(bag);
    if (!copy) continue;
    for (const key of Object.keys(copy)) {
      if (key === 'currency') continue;
      if (FLAG_KEYS.has(key)) {
        // Only a real boolean is a flag; anything else is dropped so a malformed value can never hide the block.
        if (typeof copy[key] !== 'boolean') delete copy[key];
        continue;
      }
      copy[key] = toMoneyAmount(copy[key]);
    }
  }
  for (const [bag, key] of NUMERIC_PATHS) {
    const source = result[bag];
    if (!isPlainObject(source) || !(key in source)) continue;
    const copy = ensureBag(bag);
    if (copy) copy[key] = toMoneyAmount(copy[key]);
  }
  return result;
}
